import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { BACKGROUND_CHECK_STATUSES } from "sr/constants/common";
import { getCardDetails } from "sr/utils/api/strip/getCardDetails";
import {
  downloadCleanerBackgroundCheckReport,
  downloadCleanerBackgroundCheckReportDocument,
  getCleanerLatestBackgroundCheck,
  startCleanerBackgroundCheck,
} from "sr/utils/api/checkr/cleanerCheckr";
import { getCheckrPackages } from "sr/utils/api/checkr/companyContractorCheckr";
import {
  CheckrPackageSelectionDialog,
  type CheckrPackage,
} from "app/pages/component/CheckrPackageSelectionDialog";

type PaymentCard = {
  id: string;
  card?: { last4?: string; brand?: string };
  isDefault?: boolean;
};

type BackgroundCheck = {
  status?: string;
  result?: string;
  package?: string;
  completedAt?: string;
  expiresAt?: string;
  reportAvailable?: boolean;
  profileReady?: boolean;
  profileValidationMessage?: string | null;
  payment?: {
    packageTitle?: string;
    status?: string;
    amount?: number;
    currencySymbol?: string;
    date?: string | null;
  } | null;
};

const isBackgroundCheckStartBlocked = (status?: string) =>
  [
    BACKGROUND_CHECK_STATUSES.INVITED,
    BACKGROUND_CHECK_STATUSES.IN_PROGRESS,
    BACKGROUND_CHECK_STATUSES.CLEAR,
    BACKGROUND_CHECK_STATUSES.CONSIDER,
  ].includes(status?.trim().toUpperCase() || "");

const paymentStatusLabel = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "hold_created":
    case "hold":
      return "On hold";
    case "captured":
      return "Paid";
    case "requires_reauth":
      return "Action required";
    case "hold_failed":
      return "Authorization failed";
    case "cancelled":
      return "Cancelled";
    default:
      return status
        ? status.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
        : "Unknown";
  }
};

const backgroundStatusLabel = (status?: string) =>
  status
    ? Object.entries(BACKGROUND_CHECK_STATUSES).find(
        ([, value]) => value === status,
      )?.[0]
        ?.replaceAll("_", " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase()) ||
      status
        .replaceAll("_", " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Unknown";

export default function CleanerBackgroundCheck() {
  const [backgroundCheck, setBackgroundCheck] =
    useState<BackgroundCheck | null>(null);
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [packages, setPackages] = useState<CheckrPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<CheckrPackage | null>(
    null,
  );
  const [selectedCard, setSelectedCard] = useState<PaymentCard | null>(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadBackgroundCheck = async () => {
    const response = await getCleanerLatestBackgroundCheck();
    setBackgroundCheck(response?.data || null);
  };

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        const [checkResponse, cardResponse] = await Promise.all([
          getCleanerLatestBackgroundCheck(),
          getCardDetails(),
        ]);
        if (!active) return;
        setBackgroundCheck(checkResponse?.data || null);
        setCards(cardResponse?.data?.data || []);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "Unable to load background check details",
        );
      } finally {
        if (active) setLoaded(true);
      }
    };
    loadData();
    return () => {
      active = false;
    };
  }, []);

  const openPlanDialog = async () => {
    console.log("openPlanDialog called", backgroundCheck?.status +" " + isBackgroundCheckStartBlocked(backgroundCheck?.status));
    if (isBackgroundCheckStartBlocked(backgroundCheck?.status)) {
      toast.info(
        `A background check is already ${backgroundCheck?.status?.toLowerCase()}.`,
      );
      return;
    }

    setLoading(true);
    try {
      const cardResponse = await getCardDetails();
      const savedCards: PaymentCard[] = cardResponse?.data?.data || [];
      setCards(savedCards);
      if (savedCards.length === 0) {
        toast.error(
          "Please add a card in Payment Settings before starting a background check.",
        );
        return;
      }
      if (!backgroundCheck?.profileReady) {
        toast.error(
          backgroundCheck?.profileValidationMessage ||
            "Please complete your first name, last name, email, street address, city, state, and country in your profile.",
        );
        return;
      }
      const purchaseTermExpired =
        !!backgroundCheck?.expiresAt &&
        moment(backgroundCheck.expiresAt).isValid() &&
        moment().isSameOrAfter(moment(backgroundCheck.expiresAt));
      if (
        backgroundCheck?.status &&
        !["INVITED", "EXPIRED"].includes(
          backgroundCheck.status.toUpperCase(),
        ) &&
        !purchaseTermExpired
      ) {
        toast.info(
          `A background check is already ${backgroundCheck.status.toLowerCase()}.`,
        );
        return;
      }
      const response = await getCheckrPackages();
      setPackages(response?.data || []);
      setShowPlanDialog(true);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Unable to load Checkr packages",
      );
    } finally {
      setLoading(false);
    }
  };

  const selectPackage = (checkrPackage: CheckrPackage) => {
    setSelectedPackage(checkrPackage);
    setSelectedCard(cards.find((card) => card.isDefault) || cards[0] || null);
    setShowPlanDialog(false);
    setShowCardDialog(true);
  };

  const confirmBackgroundCheck = async () => {
    if (isBackgroundCheckStartBlocked(backgroundCheck?.status)) {
      setShowPlanDialog(false);
      setShowCardDialog(false);
      toast.info(
        `A background check is already ${backgroundCheck?.status?.toLowerCase()}.`,
      );
      return;
    }

    if (!selectedPackage || !selectedCard) {
      toast.error("Select a package and payment card to continue.");
      return;
    }
    setLoading(true);
    try {
      const response = await startCleanerBackgroundCheck({
        packageId: selectedPackage._id,
        paymentMethodId: selectedCard.id,
      });
      if (!response?.success) {
        throw new Error(
          response?.message || "Failed to start the background check",
        );
      }
      toast.success("Background check invitation sent.");
      setShowCardDialog(false);
      try {
        await loadBackgroundCheck();
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "The invitation was sent, but the latest status could not be refreshed.",
        );
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to start the background check",
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    setLoading(true);
    try {
      const response = await downloadCleanerBackgroundCheckReport();
      const documents: Array<{ index: number; filename: string }> =
        response?.data || [];
      if (documents.length === 0) {
        toast.error("No PDF reports are available for this background check.");
        return;
      }
      for (const reportDocument of documents) {
        const file = await downloadCleanerBackgroundCheckReportDocument(
          reportDocument.index,
        );
        const url = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        link.download = reportDocument.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Unable to download the background check report",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (checkrPackage: CheckrPackage) =>
    `${checkrPackage.currencySymbol || "$"}${Number(checkrPackage.price).toFixed(2)}`;
  const purchaseTermExpired =
    !!backgroundCheck?.expiresAt &&
    moment(backgroundCheck.expiresAt).isValid() &&
    moment().isSameOrAfter(moment(backgroundCheck.expiresAt));
  const canStartBackgroundCheck =
    !backgroundCheck?.status ||
    ["INVITED", "EXPIRED"].includes(backgroundCheck.status.toUpperCase()) ||
    purchaseTermExpired ||
    isBackgroundCheckStartBlocked(backgroundCheck?.status);

  return (
    <section
      id="checkr-content"
      className="mt-8 inline-block w-full rounded-[18px] border border-gray-border bg-white px-6 pb-8 pt-5 text-black sm:px-10"
    >
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[18px] font-bold">
            Enable Checkr Background Checks
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Choose a Checkr package to start your background check. Your
            selected card will be temporarily authorized and charged once you
            begin the Checkr process.
          </p>
        </div>
        {canStartBackgroundCheck && (
          <button
            type="button"
            onClick={openPlanDialog}
            disabled={
              !loaded ||
              loading ||
              isBackgroundCheckStartBlocked(backgroundCheck?.status)
            }
            className="shrink-0 rounded-md bg-[#0078ae] px-4 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Please wait..." : "Start Background Check"}
          </button>
        )}
      </div>

      {backgroundCheck?.status && (
        <div className="rounded-md bg-gray-50 p-4">
          <div className="flex flex-col gap-3">
            <p>
              <span className="font-semibold">Requested plan:</span>{" "}
              {backgroundCheck.payment?.packageTitle ||
                backgroundCheck.package ||
                "Checkr package"}
            </p>
            <p>
              <span className="font-semibold">Background check status:</span>{" "}
              {backgroundStatusLabel(backgroundCheck.status)}
            </p>
            {backgroundCheck.payment && (
              <>
                <p>
                  <span className="font-semibold">Payment status:</span>{" "}
                  {paymentStatusLabel(backgroundCheck.payment.status)}
                  {typeof backgroundCheck.payment.amount === "number" &&
                    ` (${backgroundCheck.payment.currencySymbol || "$"}${backgroundCheck.payment.amount.toFixed(2)})`}
                </p>
                {backgroundCheck.payment.status?.toLowerCase() ===
                  "captured" &&
                  backgroundCheck.payment.date && (
                    <p className="text-gray-600">
                      <span className="font-semibold text-gray-700">
                        Payment date and time:
                      </span>{" "}
                      {moment(backgroundCheck.payment.date).format(
                        "DD/MM/YYYY h:mm A",
                      )}
                    </p>
                  )}
              </>
            )}
            {backgroundCheck.completedAt && (
              <p className="text-sm text-gray-600">
                Background check Completed:{" "}
                {moment(backgroundCheck.completedAt).format("DD/MM/YYYY")}
              </p>
            )}
            {backgroundCheck.expiresAt && (
              <p className="text-sm text-gray-600">
                Purchase expiry date:{" "}
                {moment(backgroundCheck.expiresAt).format("DD/MM/YYYY")}
              </p>
            )}
            {backgroundCheck.reportAvailable && (
              <button
                type="button"
                onClick={downloadReport}
                disabled={loading}
                className="mt-1 w-fit rounded-md border border-[#0078ae] px-4 py-2 font-medium text-[#0078ae] disabled:opacity-50"
              >
                Download report
              </button>
            )}
          </div>
        </div>
      )}

      {showPlanDialog && (
        <CheckrPackageSelectionDialog
          packages={packages}
          onClose={() => setShowPlanDialog(false)}
          onSelectPackage={selectPackage}
        />
      )}

      {showCardDialog && selectedPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-xl rounded-lg bg-white p-6">
            <h3 className="mb-4 text-xl font-semibold">
              Select a payment card
            </h3>
            <div className="mb-5 space-y-3">
              {cards.map((card) => (
                <label
                  key={card.id}
                  className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 p-3"
                >
                  <input
                    type="radio"
                    name="cleaner-checkr-payment-card"
                    checked={selectedCard?.id === card.id}
                    onChange={() => setSelectedCard(card)}
                  />
                  <span>
                    {card.card?.brand || "Card"} ending in{" "}
                    {card.card?.last4 || "----"}
                    {card.isDefault ? " (Default)" : ""}
                  </span>
                </label>
              ))}
            </div>
            <p className="mb-5 rounded-md bg-gray-50 p-4 text-sm">
              We’ll place a temporary authorization for{" "}
              {formatPrice(selectedPackage)} on the card ending in{" "}
              {selectedCard?.card?.last4 || "----"}. The payment will be
              captured once the contractor begins the background check.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCardDialog(false)}
                className="rounded-md border border-gray-300 px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmBackgroundCheck}
                disabled={loading || !selectedCard}
                className="rounded-md bg-[#0078ae] px-4 py-2 font-semibold text-white disabled:opacity-50"
              >
                {loading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
